import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid2,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { requestEmailVerify } from "backend";
import LoadingDoneButton from "components/Buttons/LoadingDoneButton";
import Captcha from "components/Login/Captcha";
import { asyncSignUp } from "components/Login/Redux/LoginActions";
import { LoginStatus } from "components/Login/Redux/LoginReduxTypes";
import { reset } from "rootRedux/actions";
import { useAppDispatch, useAppSelector } from "rootRedux/hooks";
import { type StoreState } from "rootRedux/types";
import router from "router/browserRouter";
import { Path } from "types/path";
import { RuntimeConfig } from "types/runtimeConfig";
import { NormalizedTextField } from "utilities/fontComponents";
import {
  meetsPasswordRequirements,
  meetsUsernameRequirements,
  normalizeEmail,
} from "utilities/userUtilities";

export enum SignupField {
  Email = "email",
  Name = "name",
  Password1 = "password1",
  Password2 = "password2",
  Username = "username",
}

type SignupError = Record<SignupField, boolean>;
export type SignupText = Record<SignupField, string>;

const defaultSignupError: SignupError = {
  [SignupField.Email]: false,
  [SignupField.Name]: false,
  [SignupField.Password1]: false,
  [SignupField.Password2]: false,
  [SignupField.Username]: false,
};
const defaultSignupText: SignupText = {
  [SignupField.Email]: "",
  [SignupField.Name]: "",
  [SignupField.Password1]: "",
  [SignupField.Password2]: "",
  [SignupField.Username]: "",
};

export const signupFieldTextId: SignupText = {
  [SignupField.Email]: "login.email",
  [SignupField.Name]: "login.name",
  [SignupField.Password1]: "login.password",
  [SignupField.Password2]: "login.confirmPassword",
  [SignupField.Username]: "login.username",
};

export const signupFieldId: SignupText = {
  [SignupField.Email]: "signup-email-field",
  [SignupField.Name]: "signup-name-field",
  [SignupField.Password1]: "signup-password1-field",
  [SignupField.Password2]: "signup-password2-field",
  [SignupField.Username]: "signup-username-field",
};

interface SignupProps {
  onSignup?: () => void;
}

/** The Signup page (also used for ProjectInvite) */
export default function Signup(props: SignupProps): ReactElement {
  const dispatch = useAppDispatch();

  const { error, signupStatus } = useAppSelector(
    (state: StoreState) => state.loginState
  );

  const [fieldError, setFieldError] = useState<SignupError>(defaultSignupError);
  const [fieldText, setFieldText] = useState<SignupText>(defaultSignupText);
  const [isVerified, setIsVerified] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const search = window.location.search;
    const email = new URLSearchParams(search).get("email");
    if (email) {
      setFieldText((prev) => ({ ...prev, [SignupField.Email]: email }));
    }
    dispatch(reset());
  }, [dispatch]);

  const errorField = (field: SignupField, error: boolean): void => {
    setFieldError((prev) => ({ ...prev, [field]: error }));
  };

  const checkUsername = (): void => {
    const username = fieldText[SignupField.Username].trim();
    errorField(SignupField.Username, !meetsUsernameRequirements(username));
  };

  const checkPassword1 = (): void => {
    const password1 = fieldText[SignupField.Password1].trim();
    errorField(SignupField.Password1, !meetsPasswordRequirements(password1));
  };

  const checkPassword2 = (): void => {
    const password1 = fieldText[SignupField.Password1].trim();
    const password2 = fieldText[SignupField.Password2].trim();
    errorField(SignupField.Password2, password1 !== password2);
  };

  const updateField = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: SignupField
  ): void => {
    setFieldText((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const signUp = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Trim whitespace off fields.
    const name = fieldText[SignupField.Name].trim();
    const username = fieldText[SignupField.Username].trim();
    const email = normalizeEmail(fieldText[SignupField.Email]);
    const password1 = fieldText[SignupField.Password1].trim();
    const password2 = fieldText[SignupField.Password2].trim();

    // Check for bad field values.
    const err: SignupError = {
      [SignupField.Name]: !name,
      [SignupField.Username]: !meetsUsernameRequirements(username),
      [SignupField.Email]: !email,
      [SignupField.Password1]: !meetsPasswordRequirements(password1),
      [SignupField.Password2]: password1 !== password2,
    };

    if (Object.values(err).some((e) => e)) {
      setFieldError(err);
    } else {
      const onLogin = RuntimeConfig.getInstance().emailServicesEnabled()
        ? async () => await requestEmailVerify(email)
        : undefined;
      await dispatch(
        asyncSignUp(name, username, email, password1, props.onSignup, onLogin)
      );
    }
  };

  const defaultTextFieldProps = (field: SignupField): TextFieldProps => ({
    error: fieldError[field],
    id: signupFieldId[field],
    label: t(signupFieldTextId[field]),
    margin: "normal",
    onChange: (e) => updateField(e, field),
    required: true,
    slotProps: { htmlInput: { maxLength: 100 } },
    style: { width: "100%" },
    value: fieldText[field],
    variant: "outlined",
  });

  return (
    <Grid2 container justifyContent="center">
      <Card sx={{ width: 450 }}>
        {/* Title */}
        <CardHeader
          title={
            <Typography align="center" variant="h5">
              {t("login.signUpNew")}
            </Typography>
          }
        />

        <CardContent>
          <form onSubmit={signUp}>
            <Stack spacing={2}>
              {/* Name field */}
              <NormalizedTextField
                {...defaultTextFieldProps(SignupField.Name)}
                autoComplete="name"
                autoFocus
                helperText={
                  fieldError[SignupField.Name] ? t("login.required") : undefined
                }
              />

              {/* Username field */}
              <NormalizedTextField
                {...defaultTextFieldProps(SignupField.Username)}
                autoComplete="username"
                helperText={t("login.usernameRequirements")}
                onBlur={() => checkUsername()}
              />

              {/* Email field */}
              {/* Don't use NormalizedTextField for type="email".
              At best, it doesn't normalize, because of the punycode. */}
              <TextField
                {...defaultTextFieldProps(SignupField.Email)}
                autoComplete="email"
                type="email" // silently converts input to punycode
              />

              {/* Password field */}
              <NormalizedTextField
                {...defaultTextFieldProps(SignupField.Password1)}
                autoComplete="new-password"
                helperText={t("login.passwordRequirements")}
                onBlur={() => checkPassword1()}
                type="password"
              />

              {/* Confirm Password field */}
              <NormalizedTextField
                {...defaultTextFieldProps(SignupField.Password2)}
                autoComplete="new-password"
                helperText={
                  fieldError[SignupField.Password2]
                    ? t("login.confirmPasswordError")
                    : undefined
                }
                onBlur={() => checkPassword2()}
                type="password"
              />

              {/* "Failed to sign up" */}
              {!!error && (
                <Typography sx={{ color: "error.main" }} variant="body2">
                  {t(error)}
                </Typography>
              )}

              <Captcha setSuccess={setIsVerified} />

              {/* Back-to-login and Sign-up buttons */}
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  onClick={() => router.navigate(Path.Login)}
                  variant="outlined"
                >
                  {t("login.backToLogin")}
                </Button>

                <LoadingDoneButton
                  disabled={!isVerified}
                  done={signupStatus === LoginStatus.Success}
                  doneText={t("login.signUpSuccess")}
                  loading={signupStatus === LoginStatus.InProgress}
                >
                  {t("login.signUp")}
                </LoadingDoneButton>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Grid2>
  );
}
